var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from 'root/src/client/logic/helpers/useStore';
import { useQueryClient } from '@tanstack/react-query';
import getFileHash from 'root/src/client/util/getFileHash';
import uploadFileToS3 from 'root/src/client/util/uploadFile';
import { ASSET_FILE_MAX_SIZE } from 'root/src/shared/constants/fileSizes';
import toast from 'react-hot-toast';
export const ASSET_UPLOAD_MUTATION_KEY = ['assetUpload'];
const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.svg', '.mp4'];
const UPLOAD_TTL = 1440;
const PROGRESS_INTERVAL_MS = 300;
function hasValidExtension(file) {
    var _a;
    const ext = `.${(_a = file.name.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase()}`;
    return ACCEPTED_EXTENSIONS.includes(ext);
}
export function useAssetUploads(options) {
    const onSuccessRef = useRef(options.onSuccess);
    onSuccessRef.current = options.onSuccess;
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const [uploads, setUploads] = useState({});
    const uploadsRef = useRef(uploads);
    uploadsRef.current = uploads;
    const timersRef = useRef({});
    // Simulated progress: run a single interval that advances all active uploads
    const activeUploadCount = Object.values(uploads).filter(e => e.status === 'uploading').length;
    useEffect(() => {
        if (activeUploadCount === 0) {
            return undefined;
        }
        const timer = setInterval(() => {
            setUploads((prev) => {
                let changed = false;
                const next = Object.assign({}, prev);
                for (const [key, entry] of Object.entries(next)) {
                    if (entry.status === 'uploading' && entry.progress < 0.9) {
                        changed = true;
                        const increment = (0.9 - entry.progress) * 0.15;
                        next[key] = Object.assign(Object.assign({}, entry), { progress: Math.min(entry.progress + increment, 0.9) });
                    }
                }
                return changed ? next : prev;
            });
        }, PROGRESS_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [activeUploadCount]);
    const doUpload = useCallback((entry) => __awaiter(this, void 0, void 0, function* () {
        const mutation = queryClient.getMutationCache().build(queryClient, {
            mutationKey: ASSET_UPLOAD_MUTATION_KEY,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            mutationFn: (_variables) => __awaiter(this, void 0, void 0, function* () {
                const fileHash = yield getFileHash(entry.file);
                const filePath = yield uploadFileToS3(entry.file, dispatch, UPLOAD_TTL);
                const url = `https://${filePath}`;
                return { url, fileName: entry.file.name, fileHash };
            }),
        });
        try {
            const result = yield mutation.execute(undefined);
            setUploads((prev) => {
                const next = Object.assign({}, prev);
                delete next[entry.id];
                return next;
            });
            onSuccessRef.current(entry.slotIndex, result, entry.file);
        }
        catch (err) {
            console.error(err);
            toast.error(`Failed to upload ${entry.file.name}`, {
                position: 'bottom-center',
            });
            setUploads((prev) => {
                const existing = prev[entry.id];
                if (!existing) {
                    return prev;
                }
                return Object.assign(Object.assign({}, prev), { [entry.id]: Object.assign(Object.assign({}, existing), { status: 'error', error: err instanceof Error ? err.message : 'Upload failed' }) });
            });
        }
    }), [dispatch, queryClient]);
    const validateAndFilterFiles = useCallback((files, remainingSlots) => {
        const validFiles = files.filter((file) => {
            if (!hasValidExtension(file)) {
                toast.error(`${file.name} has an unsupported format`, {
                    position: 'bottom-center',
                });
                return false;
            }
            if (file.size > ASSET_FILE_MAX_SIZE) {
                toast.error(`${file.name} is too large, maximum size is 100MB`, {
                    position: 'bottom-center',
                });
                return false;
            }
            return true;
        }).slice(0, remainingSlots);
        const skipped = files.length - validFiles.length;
        if (skipped > 0 && files.length > remainingSlots) {
            toast(`Only ${validFiles.length} file${validFiles.length !== 1 ? 's' : ''} added, ${skipped} skipped — maximum 10 assets`, {
                position: 'bottom-center',
            });
        }
        return validFiles;
    }, []);
    const uploadFiles = useCallback((files, startIndex) => {
        const entries = files.map((file, i) => ({
            id: crypto.randomUUID(),
            file,
            slotIndex: startIndex + i,
            status: 'uploading',
            progress: 0,
        }));
        setUploads((prev) => {
            const next = Object.assign({}, prev);
            for (const entry of entries) {
                next[entry.id] = entry;
            }
            return next;
        });
        for (const entry of entries) {
            void doUpload(entry);
        }
        return entries;
    }, [doUpload]);
    const uploadFile = useCallback((file, slotIndex) => {
        if (file.size > ASSET_FILE_MAX_SIZE) {
            toast.error('File size is too large, maximum size is 100MB', {
                position: 'bottom-center',
            });
            return undefined;
        }
        if (!hasValidExtension(file)) {
            toast.error(`${file.name} has an unsupported format`, {
                position: 'bottom-center',
            });
            return undefined;
        }
        const entry = {
            id: crypto.randomUUID(),
            file,
            slotIndex,
            status: 'uploading',
            progress: 0,
        };
        setUploads((prev) => {
            return Object.assign(Object.assign({}, prev), { [entry.id]: entry });
        });
        void doUpload(entry);
        return entry;
    }, [doUpload]);
    const retryUpload = useCallback((uploadId) => {
        const entry = uploadsRef.current[uploadId];
        if (!entry || entry.status !== 'error') {
            return;
        }
        const retried = Object.assign(Object.assign({}, entry), { status: 'uploading', error: undefined, progress: 0 });
        setUploads((prev) => {
            return Object.assign(Object.assign({}, prev), { [uploadId]: retried });
        });
        void doUpload(retried);
    }, [doUpload]);
    const dismissUpload = useCallback((uploadId) => {
        setUploads((prev) => {
            const next = Object.assign({}, prev);
            delete next[uploadId];
            return next;
        });
    }, []);
    const getSlotState = useCallback((slotIndex) => {
        for (const entry of Object.values(uploads)) {
            if (entry.slotIndex === slotIndex) {
                return entry;
            }
        }
        return undefined;
    }, [uploads]);
    // Upload state for one asset VALUE, matched on the File identity instead of a slot index. Removing
    // or drag-reordering an asset mid-upload shifts every index after it, which made the index lookup
    // attach one tile's progress (and, on success, its uploaded url) to another tile.
    const getFileState = useCallback((asset) => {
        if (!(asset instanceof File)) {
            return undefined;
        }
        return Object.values(uploads).find(entry => entry.file === asset);
    }, [uploads]);
    const isUploading = Object.values(uploads).some(e => e.status === 'uploading');
    const hasErrors = Object.values(uploads).some(e => e.status === 'error');
    // Cleanup timers on unmount
    useEffect(() => () => {
        for (const timer of Object.values(timersRef.current)) {
            clearInterval(timer);
        }
    }, []);
    return {
        uploadFiles,
        uploadFile,
        retryUpload,
        dismissUpload,
        getSlotState,
        getFileState,
        isUploading,
        hasErrors,
        validateAndFilterFiles,
    };
}
